// StudioThemeDesigner.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { useTheme, ThemeMode } from 'context/ThemeContext';
import {
  THEME_COLOR_TOKENS,
  THEME_RECIPES,
  THEME_TOKEN_GROUPS,
  resolveTheme,
} from 'context/theme';
import { buildThemeCssVariables } from 'context/themeVariables';
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalForm from 'components/modal/form/ModalForm';
import Icon from 'svg/Icon';
import SettingsDisplayPane from 'main/sidebar/switcher/popup/settings/components/SettingsDisplayPane';
import {
  savePersonalThemePack,
  subscribeToThemePack,
} from 'services/theme/ThemePackService';
import {
  applyLibraryPackToBase,
  normalizeBaseTheme,
  saveBaseTheme,
  subscribeToBaseTheme,
} from 'services/theme/BaseThemeService';
import {
  normalizeThemePack,
  toResolvableThemePack,
} from 'services/theme/themePackModel';
import { hexToRgba } from 'utilities/Colors';

import './StudioThemeDesigner.css';
import 'main/sidebar/switcher/popup/settings/SettingsModal.css';

const GROUP_LABELS = {
  [THEME_TOKEN_GROUPS.SURFACES]: 'Surfaces',
  [THEME_TOKEN_GROUPS.TEXT]: 'Text',
  [THEME_TOKEN_GROUPS.BUTTONS]: 'Buttons',
  [THEME_TOKEN_GROUPS.ACCENT]: 'Accent',
  [THEME_TOKEN_GROUPS.STATUS]: 'Status',
  [THEME_TOKEN_GROUPS.ORBIT]: 'Orbit',
};

function clonePack(pack) {
  return JSON.parse(JSON.stringify(pack));
}

function packsEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Window event used by the explorer "Rename…" action to focus the name field. */
export const FOCUS_THEME_NAME_EVENT = 'orbital:focus-theme-name';
/** sessionStorage key covering the case where the designer tab mounts fresh. */
export const FOCUS_THEME_NAME_STORAGE_KEY = 'orbital.studio.focusThemeName';

/**
 * StudioThemeDesigner
 *
 * Stage + token inspector for a personal library pack or base theme.
 *
 * @param {{
 *   themeRef: { source?: string, packKey?: string, baseKey?: string },
 *   userKey: string,
 *   studioBases?: array,
 *   themeLibraryPacks?: array,
 *   allowLibraryActions?: boolean,
 * }} props
 */
const StudioThemeDesigner = ({
  themeRef = null,
  userKey = '',
  studioBases = [],
  themeLibraryPacks = [],
  allowLibraryActions = true,
}) => {
  const {
    theme,
    themeMode,
    baseDefaultThemeMode,
    setBaseDefaultThemeMode,
  } = useTheme();

  const source = String(themeRef?.source || '').trim() === 'base'
    ? 'base'
    : 'library';
  const packKey = String(themeRef?.packKey || '').trim();
  const baseKey = String(themeRef?.baseKey || '').trim();

  const [savedPack, setSavedPack] = useState(null);
  const [draftPack, setDraftPack] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [selectedTargetBaseKey, setSelectedTargetBaseKey] = useState('');
  const [selectedReplacePackKey, setSelectedReplacePackKey] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const dirtyRef = useRef(false);
  const nameInputRef = useRef(null);

  const editMode = normalizeThemeModeSafe(themeMode);

  const focusNameField = useCallback(() => {
    const input = nameInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, []);

  const applyRemotePack = useCallback((next) => {
    setSavedPack(next);
    if (!dirtyRef.current) {
      setDraftPack(clonePack(next));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    setLoadError('');
    setSavedPack(null);
    setDraftPack(null);
    dirtyRef.current = false;

    if (source === 'base') {
      if (!baseKey) {
        setLoadError('Missing base.');
        setIsLoading(false);
        return undefined;
      }

      return subscribeToBaseTheme(
        baseKey,
        (baseTheme) => {
          const next = baseTheme || normalizeBaseTheme({
            name: 'Base theme',
            modes: {},
          }, baseKey);
          applyRemotePack(next);
        },
        (error) => {
          setLoadError(error?.message || 'Failed to load base theme.');
          setIsLoading(false);
        },
      );
    }

    if (!packKey || !userKey) {
      setLoadError('Missing theme pack.');
      setIsLoading(false);
      return undefined;
    }

    return subscribeToThemePack(
      packKey,
      (found) => {
        if (!found || String(found.userKey || '').trim() !== String(userKey || '').trim()) {
          setLoadError('Theme not found.');
          setSavedPack(null);
          setDraftPack(null);
          setIsLoading(false);
          return;
        }
        applyRemotePack(found);
      },
      (error) => {
        setLoadError(error?.message || 'Failed to load theme.');
        setIsLoading(false);
      },
    );
  }, [applyRemotePack, packKey, source, userKey, baseKey]);

  const isDirty = useMemo(() => {
    if (!savedPack || !draftPack) return false;
    return !packsEqual(savedPack, draftPack);
  }, [draftPack, savedPack]);

  useEffect(() => {
    dirtyRef.current = isDirty;
  }, [isDirty]);

  // Focus the name field when the explorer "Rename…" action targets this pack.
  useEffect(() => {
    if (source !== 'library' || !packKey) return undefined;

    const handleFocusRequest = (event) => {
      if (String(event?.detail?.packKey || '').trim() === packKey) {
        focusNameField();
      }
    };

    window.addEventListener(FOCUS_THEME_NAME_EVENT, handleFocusRequest);
    return () => window.removeEventListener(FOCUS_THEME_NAME_EVENT, handleFocusRequest);
  }, [focusNameField, packKey, source]);

  // Cover the fresh-mount case where the event fires before the listener attaches.
  useEffect(() => {
    if (source !== 'library' || !packKey || isLoading || !draftPack) return;

    let pending = null;
    try {
      pending = window.sessionStorage.getItem(FOCUS_THEME_NAME_STORAGE_KEY);
    } catch (error) {
      pending = null;
    }

    if (String(pending || '').trim() === packKey) {
      try {
        window.sessionStorage.removeItem(FOCUS_THEME_NAME_STORAGE_KEY);
      } catch (error) {
        // ignore
      }
      focusNameField();
    }
  }, [draftPack, focusNameField, isLoading, packKey, source]);

  const stageTheme = useMemo(() => {
    if (!draftPack) return null;
    return resolveTheme(toResolvableThemePack(draftPack), editMode);
  }, [draftPack, editMode]);

  const stageCssVariables = useMemo(() => {
    if (!stageTheme) return {};
    return buildThemeCssVariables(stageTheme);
  }, [stageTheme]);

  const tokensByGroup = useMemo(() => {
    const groups = {};
    THEME_COLOR_TOKENS.forEach((token) => {
      const groupKey = token.group || 'other';
      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(token);
    });
    return groups;
  }, []);

  const baseLabel = useMemo(() => {
    const match = (Array.isArray(studioBases) ? studioBases : [])
      .find((entry) => String(entry?.key || '').trim() === baseKey);
    return String(match?.label || 'Base').trim() || 'Base';
  }, [studioBases, baseKey]);

  const applyTargets = useMemo(
    () => (Array.isArray(studioBases) ? studioBases : []),
    [studioBases],
  );

  const replacePackOptions = useMemo(
    () => (Array.isArray(themeLibraryPacks) ? themeLibraryPacks : []),
    [themeLibraryPacks],
  );

  const handleTokenChange = useCallback((tokenId, value) => {
    setDraftPack((previous) => {
      if (!previous) return previous;
      const next = clonePack(previous);
      if (!next.modes) next.modes = {};
      if (!next.modes[editMode]) next.modes[editMode] = { key: editMode };
      next.modes[editMode][tokenId] = value;
      return normalizeThemePack(next);
    });
  }, [editMode]);

  const handleNameChange = useCallback((event) => {
    const value = event.target.value;
    setDraftPack((previous) => {
      if (!previous) return previous;
      return {
        ...previous,
        name: value,
      };
    });
  }, []);

  const handleDiscard = useCallback(() => {
    if (!savedPack) return;
    setDraftPack(clonePack(savedPack));
  }, [savedPack]);

  const handleSave = useCallback(async () => {
    if (!draftPack || !userKey || isSaving) return;

    setIsSaving(true);
    try {
      if (source === 'base') {
        const saved = await saveBaseTheme({
          baseKey,
          userKey,
          pack: draftPack,
          sourcePackId: draftPack.sourcePackId || null,
        });
        setSavedPack(saved);
        setDraftPack(clonePack(saved));
        toast.success('Base theme saved.');
      } else {
        const saved = await savePersonalThemePack({
          userKey,
          pack: draftPack,
        });
        setSavedPack(saved);
        setDraftPack(clonePack(saved));
        toast.success('Theme saved.');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to save theme.');
    } finally {
      setIsSaving(false);
    }
  }, [draftPack, isSaving, source, userKey, baseKey]);

  const handleApply = useCallback(async () => {
    const targetKey = String(selectedTargetBaseKey || '').trim();
    if (!targetKey || !draftPack || !userKey) return;

    setIsApplying(true);
    try {
      await applyLibraryPackToBase({
        baseKey: targetKey,
        userKey,
        libraryPack: draftPack,
      });
      toast.success('Theme applied to base.');
      setApplyOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to apply theme.');
    } finally {
      setIsApplying(false);
    }
  }, [draftPack, selectedTargetBaseKey, userKey]);

  const handleReplace = useCallback(async () => {
    const replaceKey = String(selectedReplacePackKey || '').trim();
    const libraryPack = replacePackOptions.find(
      (pack) => String(pack?.key || '').trim() === replaceKey,
    );
    if (!libraryPack || !userKey || !baseKey) return;

    setIsApplying(true);
    try {
      const saved = await applyLibraryPackToBase({
        baseKey,
        userKey,
        libraryPack,
      });
      setSavedPack(saved);
      setDraftPack(clonePack(saved));
      toast.success('Base theme replaced from library.');
      setReplaceOpen(false);
    } catch (error) {
      toast.error(error?.message || 'Failed to replace theme.');
    } finally {
      setIsApplying(false);
    }
  }, [replacePackOptions, selectedReplacePackKey, userKey, baseKey]);

  if (isLoading) {
    return (
      <div className="studio-theme-designer studio-theme-designer-loading">
        Loading theme…
      </div>
    );
  }

  if (loadError || !draftPack) {
    return (
      <div className="studio-theme-designer studio-theme-designer-loading">
        {loadError || 'Theme unavailable.'}
      </div>
    );
  }

  const modeTokenValues = draftPack.modes?.[editMode] || {};

  return (
    <div className="studio-theme-designer">
      <div className="studio-theme-designer-toolbar">
        <div className="studio-theme-designer-toolbar-title">
          {source === 'base' ? (
            <span>{`Theme · ${baseLabel}`}</span>
          ) : (
            <span
              className="studio-theme-designer-name-field"
              onClick={focusNameField}
            >
              <input
                ref={nameInputRef}
                aria-label="Theme name"
                className="studio-theme-designer-name-input"
                placeholder="Untitled theme"
                type="text"
                value={draftPack.name || ''}
                onChange={handleNameChange}
              />
              <Icon
                iconKey="edit"
                width="15"
                height="15"
              />
            </span>
          )}
          <span className="studio-theme-designer-mode-hint">
            Editing {editMode === ThemeMode.LIGHT ? 'light' : 'dark'} mode
          </span>
        </div>

        <div className="studio-theme-designer-toolbar-actions">
          {allowLibraryActions && source === 'library' ? (
            <Button
              enabled={!isApplying}
              label="Apply to base…"
              onClick={() => {
                setSelectedTargetBaseKey(String(applyTargets[0]?.key || ''));
                setApplyOpen(true);
              }}
            />
          ) : null}
          {allowLibraryActions && source === 'base' ? (
            <Button
              enabled={!isApplying && replacePackOptions.length > 0}
              label="Replace from library…"
              onClick={() => {
                setSelectedReplacePackKey(String(replacePackOptions[0]?.key || ''));
                setReplaceOpen(true);
              }}
            />
          ) : null}
          <Button
            enabled={isDirty && !isSaving}
            label="Discard"
            onClick={handleDiscard}
          />
          <Button
            enabled={isDirty && !isSaving}
            label={isSaving ? 'Saving…' : 'Save'}
            onClick={handleSave}
          />
        </div>
      </div>

      {source === 'base' ? (
        <div className="studio-theme-designer-default-display">
          <SettingsDisplayPane
            theme={theme}
            isDarkMode={baseDefaultThemeMode === ThemeMode.DARK}
            onThemeModeChange={(nextEnabled) => {
              setBaseDefaultThemeMode(nextEnabled ? ThemeMode.DARK : ThemeMode.LIGHT);
            }}
            title="Default Display"
            subtitle="Base default appearance for members"
            settingLabel="Dark Mode"
            settingDescription="Applied as this base's default when a member has not chosen their own preference. Separate from the mode you are editing above."
          />
        </div>
      ) : null}

      <div className="studio-theme-designer-body">
        <div
          className="studio-theme-designer-stage"
          style={stageCssVariables}
        >
          <ThemeStageMosaic />
        </div>

        <aside className="studio-theme-designer-inspector">
          {Object.entries(tokensByGroup).map(([groupKey, tokens]) => (
            <div className="studio-theme-designer-group" key={groupKey}>
              <div className="studio-theme-designer-group-title">
                {GROUP_LABELS[groupKey] || groupKey}
              </div>
              {tokens.map((token) => {
                const tokenValue = modeTokenValues[token.id];
                return (
                  <label className="studio-theme-designer-token-row" key={token.id}>
                    <span className="studio-theme-designer-token-label" title={token.description}>
                      {token.title}
                    </span>
                    <span
                      aria-hidden="true"
                      className="studio-theme-designer-token-swatch"
                      style={{ background: String(tokenValue || 'transparent') }}
                    />
                    <input
                      aria-label={token.title}
                      className="studio-theme-designer-token-color"
                      type="color"
                      value={colorValueToHexForPicker(tokenValue)}
                      onChange={(event) => {
                        handleTokenChange(
                          token.id,
                          applyPickerHexToTokenValue(event.target.value, tokenValue),
                        );
                      }}
                    />
                    <input
                      aria-label={`${token.title} value`}
                      className="studio-theme-designer-token-hex"
                      type="text"
                      value={String(tokenValue || '')}
                      onChange={(event) => handleTokenChange(token.id, event.target.value)}
                    />
                  </label>
                );
              })}
            </div>
          ))}

          <details className="studio-theme-designer-recipes">
            <summary>Recipes (read-only)</summary>
            <ul>
              {THEME_RECIPES.map((recipe) => (
                <li key={recipe.id}>
                  <code>{recipe.id}</code>
                  <span>{recipe.title}</span>
                </li>
              ))}
            </ul>
          </details>
        </aside>
      </div>

      <Modal
        isOpen={applyOpen}
        onClose={() => setApplyOpen(false)}
        title="Apply theme to base"
      >
        <ModalForm>
          <p>Copies this library theme onto the space as a starting fork. Later library edits will not update that space.</p>
          <label className="studio-theme-designer-modal-field">
            <span>Base</span>
            <select
              value={selectedTargetBaseKey}
              onChange={(event) => setSelectedTargetBaseKey(event.target.value)}
            >
              {applyTargets.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>
          <div className="studio-theme-designer-modal-actions">
            <Button
              enabled={!isApplying}
              label="Cancel"
              onClick={() => setApplyOpen(false)}
            />
            <Button
              enabled={!isApplying && Boolean(selectedTargetBaseKey)}
              label={isApplying ? 'Applying…' : 'Apply'}
              onClick={handleApply}
            />
          </div>
        </ModalForm>
      </Modal>

      <Modal
        isOpen={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        title="Replace from library"
      >
        <ModalForm>
          <p>Replaces this base theme with a fresh copy of a library pack.</p>
          <label className="studio-theme-designer-modal-field">
            <span>Library theme</span>
            <select
              value={selectedReplacePackKey}
              onChange={(event) => setSelectedReplacePackKey(event.target.value)}
            >
              {replacePackOptions.map((pack) => (
                <option key={pack.key} value={pack.key}>
                  {pack.name}
                </option>
              ))}
            </select>
          </label>
          <div className="studio-theme-designer-modal-actions">
            <Button
              enabled={!isApplying}
              label="Cancel"
              onClick={() => setReplaceOpen(false)}
            />
            <Button
              enabled={!isApplying && Boolean(selectedReplacePackKey)}
              label={isApplying ? 'Replacing…' : 'Replace'}
              onClick={handleReplace}
            />
          </div>
        </ModalForm>
      </Modal>
    </div>
  );
};

function normalizeThemeModeSafe(mode) {
  return mode === ThemeMode.LIGHT ? ThemeMode.LIGHT : ThemeMode.DARK;
}

function toTwoDigitHex(channel) {
  return Math.max(0, Math.min(255, Number(channel) || 0))
    .toString(16)
    .padStart(2, '0');
}

/**
 * Native <input type="color"> only accepts #rrggbb. Map rgba/hex into that form for the picker.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
function colorValueToHexForPicker(value) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const [, a, b, c] = raw;
    return `#${a}${a}${b}${b}${c}${c}`;
  }

  const rgbMatch = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return `#${toTwoDigitHex(rgbMatch[1])}${toTwoDigitHex(rgbMatch[2])}${toTwoDigitHex(rgbMatch[3])}`;
  }

  return '#000000';
}

/**
 * Preserve alpha when editing an existing rgba(...) token via the hex color picker.
 *
 * @param {string} nextHex
 * @param {string|null|undefined} previousValue
 * @returns {string}
 */
function applyPickerHexToTokenValue(nextHex, previousValue) {
  const previous = String(previousValue || '').trim();
  const alphaMatch = previous.match(/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)$/i);
  if (alphaMatch) {
    const alpha = Number(alphaMatch[1]);
    if (Number.isFinite(alpha) && alpha < 1) {
      return hexToRgba(nextHex, alpha);
    }
  }
  return nextHex;
}

function ThemeStageMosaic() {
  return (
    <div className="studio-theme-stage-mosaic">
      <div className="studio-theme-stage-tile studio-theme-stage-canvas">
        <span>Page canvas (Glow wash)</span>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-panel">
        <strong>Panel</strong>
        <p className="studio-theme-stage-body">Body text on a panel surface.</p>
        <p className="studio-theme-stage-muted">Muted supporting copy.</p>
        <p className="studio-theme-stage-faded">Faded metadata</p>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-popup">
        <div className="studio-theme-stage-popup-inner">Popup / menu</div>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-controls">
        <button className="studio-theme-stage-btn-primary" type="button">Primary</button>
        <span className="studio-theme-stage-controls-hint">Shadow uses Accent glow</span>
        <button className="studio-theme-stage-btn-ghost" type="button">Ghost</button>
        <input className="studio-theme-stage-input" defaultValue="Focused field" readOnly />
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-sidebar">
        <div className="studio-theme-stage-row">Sidebar idle</div>
        <div className="studio-theme-stage-row is-hover">Sidebar hover</div>
        <div className="studio-theme-stage-row is-selected">Sidebar selected</div>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-message">
        <div className="studio-theme-stage-message-bubble">Channel message snippet</div>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-modal">
        <div className="studio-theme-stage-modal-sheet">Modal sheet / glass</div>
      </div>
      <div className="studio-theme-stage-tile studio-theme-stage-status">
        <span className="chip chip-success">Success</span>
        <span className="chip chip-error">Error</span>
        <span className="chip chip-warning">Warning</span>
        <span className="chip chip-accent">Accent</span>
      </div>
    </div>
  );
}

StudioThemeDesigner.displayName = 'StudioThemeDesigner';

export default StudioThemeDesigner;
